import React, { useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import { MultiSelectChips, Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal from 'components/shared/StandardModal';
import { HEALTH_PLAN, PLAN_TYPE } from 'constants/classifications';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Classification } from 'models/flexible/Classification';
import {
  adminIndexClassifications,
  createClassification,
  updateClassification,
} from 'services/api/admin/classifications';
import type { AdminClassificationIndexFields, AdminClassificationShowFields } from 'services/api/fields';
import { useToastActions } from 'stores/toastStore';

type Props = {
  isOpen: boolean;
  classification: Classification<AdminClassificationShowFields>;
  title: string;
  onCancel: () => void;
  onComplete: () => void;
};

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;

const schema = yup.object({
  name: yup.string().required('Name is required'),
});

export default function ClassificationModal({ isOpen, classification, title, onCancel, onComplete }: Props) {
  const { addToast } = useToastActions();
  const isEditing = !!classification.id;

  const [name, setName] = useState(classification.name);
  const [parentClassification, setParentClassification] =
    useState<Classification<AdminClassificationIndexFields> | null>(
      classification.parentClassification as Classification<AdminClassificationIndexFields> | null
    );
  const [childClassifications, setChildClassifications] = useState<Classification<AdminClassificationIndexFields>[]>(
    classification.childClassifications as Classification<AdminClassificationIndexFields>[]
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const planTypeOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'planType', { parentClassificationId: 'null' }],
    fetchFn: adminIndexClassifications,
    params: { type: PLAN_TYPE, parentClassificationId: 'null', sortBy: 'name asc' },
  });

  const healthPlanOpts = useInfiniteOptions({
    queryKey: ['adminClassifications', 'healthPlan', 'unarchived'],
    fetchFn: adminIndexClassifications,
    params: { type: HEALTH_PLAN, archived: false, sortBy: 'name asc' },
  });

  const valid = name.trim().length > 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await schema.validate({ name }, { abortEarly: false });
      setErrors({});

      const serialized = {
        name: name ?? '',
        type: classification.classificationClass,
        parentClassificationId: parentClassification?.id ?? null,
        childClassificationIds: (childClassifications ?? []).map((c) => c.id),
      };

      if (isEditing) {
        await updateClassification(classification.id, serialized);
      } else {
        await createClassification(serialized);
      }

      addToast({ text: `${title} successfully ${isEditing ? 'updated' : 'created'}` });
      onComplete();
    } catch (e: any) {
      if (yup.ValidationError.isError(e)) {
        const fieldErrors: Record<string, string> = {};
        e.inner.forEach((err) => {
          if (err.path) fieldErrors[err.path] = err.message;
        });
        setErrors(fieldErrors);
      } else if (e?.response?.data?.errors?.name) {
        addToast({
          text: `${title} with that name already exists. ${title} must be unique and not match any existing name.`,
        });
      } else {
        addToast({
          text: `There was an error ${isEditing ? 'updating' : 'creating'} the ${title.toLowerCase()}`,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <StandardModal title={`${isEditing ? 'Edit' : 'Add'} ${title}`} onCancel={onCancel}>
      <FormContainer>
        {classification.isPlanType && (
          <InputGroup title='Plan (optional)'>
            <Select
              {...healthPlanOpts}
              value={parentClassification}
              onChange={(v) => setParentClassification(v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Plan'
              isClearable
            />
          </InputGroup>
        )}

        <InputGroup title={title} error={errors.name}>
          <Input
            value={name ?? ''}
            onChange={(e) => setName(e.target.value)}
            placeholder={title}
            $hasError={!!errors.name}
          />
        </InputGroup>

        {classification.isHealthPlan && (
          <InputGroup>
            <MultiSelectChips
              title='Associated Plan Types'
              {...planTypeOpts}
              values={childClassifications}
              onChange={(v) => setChildClassifications(v)}
              getOptionValue={getId}
              getOptionLabel={getName}
              placeholder='Plan Types'
            />
          </InputGroup>
        )}

        <Actions>
          <Button variant='ghost' onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!valid || submitting} loading={submitting}>
            Submit
          </Button>
        </Actions>
      </FormContainer>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Actions = styled(ButtonGroup)`
  justify-content: flex-end;
  margin-top: 16px;
`;
